// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { API_BASE_URL, API_TIMEOUT } from '@/config/api'
import { tokenStorage } from '@/lib/auth/tokenStorage'
import type { ApiError } from '@/types'

export class HttpError extends Error implements ApiError {
  code: string
  statusCode: number
  details?: Record<string, string[]>

  constructor(payload: ApiError) {
    super(payload.message)
    this.code = payload.code
    this.statusCode = payload.statusCode
    this.details = payload.details
    this.name = 'HttpError'
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  params?: Record<string, string | number | boolean | undefined | null>
}

function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined | null>): string {
  const url = new URL(path, API_BASE_URL)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value))
      }
    }
  }
  return url.toString()
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, params, headers, ...rest } = options

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), API_TIMEOUT)

  const accessToken = tokenStorage.getAccessToken()

  try {
    const res = await fetch(buildUrl(path, params), {
      ...rest,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        Accept: 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

    if (!res.ok) {
      const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>
      const code =
        typeof payload.code === 'string'
          ? payload.code
          : typeof payload.error === 'string'
            ? payload.error
            : 'UNKNOWN_ERROR'
      const message =
        typeof payload.message === 'string' ? payload.message : res.statusText
      const details = payload.details as Record<string, string[]> | undefined
      throw new HttpError({ statusCode: res.status, code, message, details })
    }

    if (res.status === 204) return undefined as T
    return res.json() as Promise<T>
  } finally {
    clearTimeout(timeout)
  }
}

export const apiClient = {
  get: <T>(path: string, params?: RequestOptions['params']) => request<T>(path, { method: 'GET', params }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T = void>(path: string) => request<T>(path, { method: 'DELETE' }),
}
